import { useState, useEffect, useCallback, useMemo } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';
import type { Destinations } from './useDestinations';

interface UseInternationalDestinationsReturn {
  destinations: Destinations[];
  destinationLocationOptions: string[];
  destinationMapping: Record<string, string>; // Maps destinationName to destinationCode
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching combined International destinations
 * Combines both Schengen (WorldwideElite) and Asia/Oceania destinations
 * for intelligent section detection based on user selection
 */
export const useInternationalDestinations = (): UseInternationalDestinationsReturn => {
  const [destinations, setDestinations] = useState<Destinations[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInternationalDestinations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Sequential requests through the enhanced API queue instead of parallel Promise.all
      // This prevents overwhelming the server with simultaneous requests
      const schengenResponse = await enhancedApi.cachedGet<{
        success: boolean;
        destinations: Destinations[];
      }>(
        '/api/destinations/by-scope?scope=schengen',
        {},
        15 * 60 * 1000 // 15 minutes TTL
      );

      const asiaOceaniaResponse = await enhancedApi.cachedGet<{
        success: boolean;
        destinations: Destinations[];
      }>(
        '/api/destinations/by-scope?scope=asiaOceania',
        {},
        15 * 60 * 1000 // 15 minutes TTL
      );

      const schengenDestinations = schengenResponse.success ? schengenResponse.destinations : [];
      const asiaOceaniaDestinations = asiaOceaniaResponse.success ? asiaOceaniaResponse.destinations : [];

      // Combine destinations and remove duplicates based on destinationName (more reliable than ID)
      const combinedDestinations = [...schengenDestinations, ...asiaOceaniaDestinations];
      
      // Use destinationName for deduplication instead of destinationID
      const uniqueDestinations = combinedDestinations.filter((destination, index, self) =>
        index === self.findIndex(d => d.destinationName === destination.destinationName)
      );

      // Sort alphabetically by destinationName
      const sortedDestinations = uniqueDestinations.sort((a, b) =>
        a.destinationName.localeCompare(b.destinationName)
      );

      console.log("Fetched international destinations (cached/sorted):", sortedDestinations);
      setDestinations(sortedDestinations);
    } catch (err) {
      console.error('Error fetching International destinations:', err);
      setError('Failed to fetch International destinations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInternationalDestinations();
  }, [fetchInternationalDestinations]);

  // Convert destinations to location options format for destination dropdowns
  const destinationLocationOptions = destinations.map(destination => destination.destinationName);

  // Create mapping from destinationName to destinationCode (similar to useDestinations)
  const destinationMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    destinations.forEach(destination => {
      mapping[destination.destinationName] = destination.destinationCode;
    });
    return mapping;
  }, [destinations]);

  return {
    destinations,
    destinationLocationOptions,
    destinationMapping,
    loading,
    error,
    refetch: fetchInternationalDestinations
  };
};